import React, {
  TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';

interface CustomTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  placeholder: string;
  name: string;
}

export const Textarea: React.FC<CustomTextareaProps> = ({
  placeholder,
  name,
  ...textareaProps
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasText, setHasText] = useState(false);
  const [value, setValue] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const handleFocus = useCallback(() => setIsFocused(true), []);

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      setHasText(!!event.target.value);
    },
    []
  );

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = '0px';
      const { scrollHeight, offsetHeight, clientHeight } = textAreaRef.current;
      textAreaRef.current.style.height = `${scrollHeight + offsetHeight - clientHeight}px`;
    }
  }, [textAreaRef, value]);

  const handleChange = useCallback(
    (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = evt.target?.value;
      setHasText(!!evt.target.value);
      setValue(val);
    },
    []
  );

  return (
    <div className='relative'>
      <span
        className={`pointer-events-none absolute left-1/2 top-1/2 w-full -translate-x-1/2 -translate-y-1/2 transform pb-1.5 pl-5 text-left text-gray-400 transition-opacity duration-300 ${
          isFocused || hasText ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {placeholder}
      </span>
      <textarea
        {...textareaProps}
        name={name}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        ref={textAreaRef}
        rows={1}
        value={value}
        className={classNames(
          'max-h-36 min-h-10 w-full resize-none rounded border-[1px] bg-input-bg px-5 py-4 font-normal text-input-text transition-colors duration-200 focus:resize-y focus:outline-none',
          {
            'border-input-border-focus': isFocused || hasText,
            'border-transparent': !isFocused && !hasText,
          }
        )}
      />
    </div>
  );
};
